import { Component, computed, effect, inject, OnInit, signal, viewChild } from '@angular/core';
import { Client } from '../../api/types';
import { ColumnDescription, TableComponent } from '../components/table';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import {
  SynAlertComponent,
  SynBreadcrumbComponent,
  SynBreadcrumbItemComponent,
  SynButtonComponent,
  SynIconComponent, SynTooltipComponent,
} from '@synergy-design-system/angular';
import { hasClientCreationRole } from '../../api/auth';
import { StateService } from '../../api/state';

@Component({
  selector: 'app-client-manager',
  template: `
    <syn-breadcrumb>
      <syn-breadcrumb-item routerLink="" i18n="@@home">Home</syn-breadcrumb-item>
      <syn-breadcrumb-item routerLink="/clients" i18n="@@clients">Clients</syn-breadcrumb-item>
    </syn-breadcrumb>

    <div class="flex-row gap-1">
      <h1 i18n="@@my-clients" data-e2e="heading-my-clients">My OIDC Clients</h1>
      <syn-button style="margin-top: auto" size="small"
                  [routerLink]="canCreate() ? './template' : '.'"
                  [disabled]="!canCreate()"
                  data-e2e="btn-create-new-client">
        <syn-icon name="add_fill" slot="prefix" />
        <span i18n="@@create-new-client">Create a New Client</span>
      </syn-button>
    </div>

    @if (hasIssues()) {
      <syn-alert variant="warning" open="" class="margin-top-1 margin-bottom-1">
        <syn-icon slot="icon" name="warning_fill" />
        <span i18n="@@warning-issues-for-some-clients">
          Some of your clients have security or maintenance issues. Inspect them via:
        </span>&nbsp;<a routerLink="/guard">SICK ID Guard</a>
      </syn-alert>
    }

    <ng-template #rowTemplate let-value>
      <!-- Enable type checking in the template -->
      @let client = identity(value);
      <tr (click)="openClient(client)">
        <td>{{ client.clientId }}</td>
        <td>
          {{ getPermission(client) }}
        </td>
        <td>{{ client.enabled ? '✔' : '✖' }}</td>
        <td>
          <div class="flex-row gap-05 align-center">
            {{ client.lastLogin ?? '' }}
            @if (isInactive(client)) {
              <syn-tooltip style="" i18n-content="@@client-inactive-warning"
                           content="The client has not been used for a long time. Delete the client if it is no longer needed.">
                <syn-icon style="color: var(--syn-color-warning-500); font-size: var(--syn-font-size-large);"
                          name="info_fill"
                          size="small" />
              </syn-tooltip>
            }
          </div>
        </td>
        <td>{{ client.description }}</td>
      </tr>
    </ng-template>

    <app-table
      [columns]="columns"
      [values]="clients()"
      [rowTemplate]="rowTemplate"
      [loading]="loading()"
      [clickable]="true"
      data-e2e="client-table"
      #table
    />
  `,
  styles: [
    `
      h1 {
        margin-bottom: 0;
      }

      app-table {
        width: 100%;
      }
    `,
  ],
  imports: [
    RouterLink,
    TableComponent,
    SynButtonComponent,
    SynIconComponent,
    SynBreadcrumbComponent,
    SynBreadcrumbItemComponent,
    SynTooltipComponent,
    SynAlertComponent,
  ],
})
export class ClientManagerComponent implements OnInit {
  state = inject(StateService);
  activeRoute = inject(ActivatedRoute);
  router = inject(Router);
  now = new Date();

  canCreate = signal(false);

  loading = this.state.clientsLoading;
  clients = this.state.clients;
  hasIssues = computed(() => {
    return this.state.clientIssues().length > 0;
  });

  table = viewChild<TableComponent<Client>>('table');

  columns: ColumnDescription<Client>[] = [
    {
      label: $localize`:@@client-id:Client ID`,
      getField: (client) => client.clientId,
      style: 'width: 30%',
    },
    {
      label: $localize`:@@permission:Permission`,
      getField: this.getPermission,
      style: 'width: 15%',
    },
    {
      label: $localize`:@@enabled:Enabled`,
      getField: (client) => client.enabled ? '✔' : '✖',
      style: 'width: 10%',
    },
    {
      label: $localize`:@@last-login:Last Login`,
      getField: (client) => client.description,
      style: 'width: 15%',
    },
    {
      label: $localize`:@@description:Description`,
      getField: (client) => client.description,
      style: 'width: 30%',
    },
  ];

  constructor() {
    effect(() => {
      const supportView = this.state.supportView();
      if (supportView) {
        console.log('Enabled support view');
      }

      this.state.queryClients(() => {
        this.table()?.gotoFirstPage();
        this.state.queryClientIssues();
      });
    });
  }

  ngOnInit() {
    this.canCreate.set(hasClientCreationRole());
  }

  openClient(client: Client) {
    void this.router.navigate([client.id, 'settings'], { relativeTo: this.activeRoute }).then(() => {
    });
  }

  getPermission(client: Client): string {
    switch (client.associatedRole) {
      case 'client_admin':
        return $localize`:@@client-admin:Client Admin`;
      case 'role_admin':
        return $localize`:@@role-admin:Role Admin`;
      case 'role_manager':
        return $localize`:@@role-manager:Role Manager`;
      default:
        return 'unknown';
    }
  }

  isInactive(client: Client): boolean {
    if (!client.lastLogin) {
      return true;
    }
    const lastLogin = Date.parse(client.lastLogin);
    const sinceLastLoginMs = this.now.getTime() - lastLogin;
    const sinceLastLoginDays = sinceLastLoginMs / 1000 / 60 / 60 / 24.0;
    return sinceLastLoginDays > 182;
  }

  identity(client: Client): Client {
    return client;
  }
}
